import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useDeployAuth } from "../../../context/DeployAuthContext";
import LimeLogo from "../../../assets/lime-logo-no-bg.png";
import DeployLegalFooter from "../../../components/deploy/DeployLegalFooter";

const api = axios.create({ baseURL: "", withCredentials: true });

const STATUS_STYLES = {
  active:             "text-lime-300 border-lime-700 bg-lime-900/20",
  provisioning:       "text-amber-300 border-amber-700 bg-amber-900/20",
  provisioning_db:    "text-amber-300 border-amber-700 bg-amber-900/20",
  provisioning_infra: "text-amber-300 border-amber-700 bg-amber-900/20",
  reserved:           "text-blue-300 border-blue-700 bg-blue-900/20",
  pending:            "text-blue-300 border-blue-700 bg-blue-900/20",
  degraded:           "text-red-300 border-red-700 bg-red-900/20",
  suspended:          "text-silver-dark border-gray-700",
  failed:             "text-red-400 border-red-800",
};

// Customer-facing labels for the internal snake_case store status values.
// Subscription statuses (active/trialing/past_due…) are left to capitalize as-is.
const PROJECT_STATUS_LABELS = {
  active:             "Live",
  provisioning:       "Setting up",
  provisioning_db:    "Setting up database",
  provisioning_infra: "Setting up infrastructure",
  reserved:           "Reserved",
  pending:            "Setting up",
  degraded:           "Degraded",
  suspended:          "Suspended",
  failed:             "Setup failed",
};

function StatusBadge({ status, labels }) {
  const s = (status || "pending").toLowerCase();
  const label = (labels && labels[s]) || s.replace(/_/g, " ");
  return (
    <span className={`text-xs border rounded px-2 py-0.5 capitalize ${STATUS_STYLES[s] || STATUS_STYLES.pending}`}>
      {label}
    </span>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch { return; }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className="text-xs border border-gray-700 hover:border-gray-500 text-silver-dark hover:text-silver-light rounded px-2 py-0.5 transition-colors flex-shrink-0">
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function InfoRow({ label, children }) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-gray-800 last:border-0">
      <span className="text-silver-dark text-sm w-36 flex-shrink-0">{label}</span>
      <span className="text-silver-light text-sm text-right flex-1 flex items-center justify-end gap-2 flex-wrap">
        {children}
      </span>
    </div>
  );
}

export default function StorefrontDashboardPage() {
  // Render log: catch unnecessary remounts while the subscribe CTA still exists for legacy boxes.
  console.log(`[${new Date().toISOString()}] StorefrontDashboardPage render`);
  const { customer, logout } = useDeployAuth();
  const [data, setData] = useState({ subscription: null, project: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);

  // Marketplace boost upsell
  const [offer, setOffer] = useState(null);        // { tiers, hasBillingAccount, listings }
  const [boostingKey, setBoostingKey] = useState(""); // `${listingId}:${tier}` while a charge is in flight
  const [boostMsg, setBoostMsg] = useState(null);    // { ok: boolean, text: string }
  const boostRef = useRef(null);
  const [highlightBoost, setHighlightBoost] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/api/storefront/billing/subscription");
      setData({ subscription: d.subscription || null, project: d.project || null });
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load storefront data");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOffer = useCallback(async () => {
    try {
      const { data: d } = await api.get("/api/marketplace/manage/boost-offer");
      setOffer(d);
    } catch {
      // Non-fatal — the boost card just won't render.
    }
  }, []);

  useEffect(() => { load(); loadOffer(); }, [load, loadOffer]);

  // Deep-link from the launch email (?boost=1) — scroll to and flash the boost card.
  useEffect(() => {
    if (!offer || !offer.listings || !offer.listings.length) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("boost") === "1" && boostRef.current) {
      boostRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightBoost(true);
      const t = setTimeout(() => setHighlightBoost(false), 2400);
      return () => clearTimeout(t);
    }
  }, [offer]);

  const boost = async (listingId, tier) => {
    setBoostMsg(null);
    setBoostingKey(`${listingId}:${tier}`);
    try {
      await api.post(`/api/marketplace/manage/listings/${listingId}/boost`, { tier });
      setBoostMsg({ ok: true, text: "Boost activated — your store is now featured on the marketplace." });
      await loadOffer();
    } catch (e) {
      const d = e.response?.data;
      setBoostMsg({ ok: false, text: d?.error || "Could not start the boost. Please try again." });
    } finally {
      setBoostingKey("");
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data: d } = await api.post("/api/storefront/billing/create-portal-session");
      if (d.url) window.location.href = d.url;
    } catch (e) {
      setError(e.response?.data?.error || "Could not open billing portal");
    } finally {
      setPortalLoading(false);
    }
  };

  const { subscription, project } = data;
  const projectStatus = project?.status || "pending";

  if (loading) {
    return (
      <div className="bg-black min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-lime-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-black text-silver-light min-h-[calc(100vh-4rem)] px-4 py-12">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <img src={LimeLogo} alt="Limelite" className="w-8 h-8" />
            <div>
              <p className="text-xs uppercase tracking-widest text-lime-500">Limelite Storefront</p>
              <h1 className="text-2xl font-serif font-medium">Store Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/limelite/deploy/dashboard" className="text-sm text-silver-dark hover:text-silver-light transition-colors">
              Deploy →
            </Link>
            <button onClick={logout} className="text-sm border border-gray-700 hover:border-gray-500 text-silver-dark hover:text-silver-light rounded px-3 py-1.5 transition-colors">
              Sign out
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-900/10 border border-red-900/40 rounded-lg px-4 py-3 text-sm text-red-400">
            {error}
          </div>
        )}

        <div className="space-y-6">

          {/* Store status */}
          <div className="bg-off-black border border-gray-800 rounded-xl p-6">
            <h2 className="text-sm uppercase tracking-widest text-silver-dark mb-5">Store</h2>

            {!subscription ? (
              <div>
                <p className="text-silver-dark text-sm mb-4">No active storefront subscription.</p>
                {/* /limelite/storefront redirects; do not send unpaid users into a retired €49 funnel. Current offer is the €29 platform at /limelite. */}
                <Link to="/limelite" className="inline-flex items-center bg-lime-600 hover:bg-lime-500 text-black font-bold text-sm py-2 px-5 rounded-lg transition-colors">
                  Open Limelite — €29/month platform →
                </Link>
              </div>
            ) : (
              <>
                <InfoRow label="Status"><StatusBadge status={projectStatus} labels={PROJECT_STATUS_LABELS} /></InfoRow>
                {project?.statusMessage && (
                  <InfoRow label="Info">
                    <span className="text-silver-dark text-xs">{project.statusMessage}</span>
                  </InfoRow>
                )}
                {project?.storeUrl ? (
                  <InfoRow label="Store URL">
                    <a href={project.storeUrl} target="_blank" rel="noopener noreferrer" className="text-lime-400 hover:text-lime-300 transition-colors text-xs font-mono truncate max-w-xs">
                      {project.storeUrl}
                    </a>
                    <CopyButton text={project.storeUrl} />
                  </InfoRow>
                ) : (
                  <InfoRow label="Store URL">
                    <span className="text-silver-dark text-xs italic">
                      {["pending", "provisioning_db", "provisioning_infra", "provisioning"].includes(projectStatus)
                        ? "Being provisioned — check back in ~15 minutes"
                        : "Not yet assigned"}
                    </span>
                  </InfoRow>
                )}
                {project?.adminUrl && (
                  <InfoRow label="Admin panel">
                    <a href={project.adminUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center bg-lime-600 hover:bg-lime-500 text-black font-semibold text-xs py-1.5 px-3 rounded transition-colors">
                      Open admin →
                    </a>
                    <CopyButton text={project.adminUrl} />
                  </InfoRow>
                )}
                {project?.publicIpAddress && (
                  <InfoRow label="IP">
                    <code className="text-silver-medium font-mono text-xs">{project.publicIpAddress}</code>
                    <CopyButton text={project.publicIpAddress} />
                  </InfoRow>
                )}
              </>
            )}
          </div>

          {subscription && (
            <div className="border border-lime-800/50 bg-lime-950/10 rounded-xl p-6">
              <p className="text-xs uppercase tracking-widest text-lime-400">Marketplace discovery</p>
              <h2 className="mt-2 text-lg font-medium text-silver-light">Your store isn&apos;t an island.</h2>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-silver-medium">
                Your Limelite store is built to publish its catalogue to the Limelite Marketplace, helping shoppers discover products beyond your own URL.
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <Link to="/limelite/marketplace" className="text-sm font-medium text-lime-300 hover:text-lime-200">Browse Marketplace →</Link>
                <span className="text-xs text-silver-dark">Catalogue distribution is rolling out. Checkout stays on your store.</span>
              </div>
            </div>
          )}

          {/* Marketplace boost upsell */}
          {offer && offer.listings && offer.listings.length > 0 && (
            <div
              ref={boostRef}
              className={`bg-off-black border rounded-xl p-6 transition-all duration-500 ${highlightBoost ? "border-lime-500 ring-1 ring-lime-600/40" : "border-gray-800"}`}
            >
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-sm uppercase tracking-widest text-silver-dark">Marketplace boost</h2>
                <span className="text-[11px] text-lime-500 uppercase tracking-widest">Get discovered</span>
              </div>
              <p className="text-silver-dark text-sm mb-5">
                Feature your store on the Limelite Marketplace and put it in front of shoppers browsing for products like yours.
              </p>

              {boostMsg && (
                <div className={`mb-4 rounded-lg px-4 py-3 text-sm border ${boostMsg.ok ? "bg-lime-900/10 border-lime-900/40 text-lime-300" : "bg-red-900/10 border-red-900/40 text-red-400"}`}>
                  {boostMsg.text}
                </div>
              )}

              {offer.listings.map((listing) => (
                <div key={listing.id} className="border-b border-gray-800 last:border-0 py-4 first:pt-0">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <p className="text-silver-light text-sm font-medium truncate">{listing.name}</p>
                      <p className="text-silver-dark text-xs mt-0.5">
                        {listing.viewCount} {listing.viewCount === 1 ? "view" : "views"}
                        {listing.sourceType === "storefront" ? " · Store" : ""}
                      </p>
                    </div>
                    {listing.featured && (
                      <span className="text-xs border rounded px-2 py-0.5 text-lime-300 border-lime-700 bg-lime-900/20 capitalize flex-shrink-0">
                        {listing.featuredTier} · until {new Date(listing.featuredUntil).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {offer.tiers.map((t) => {
                      const busy = boostingKey === `${listing.id}:${t.id}`;
                      return (
                        <button
                          key={t.id}
                          onClick={() => boost(listing.id, t.id)}
                          disabled={Boolean(boostingKey)}
                          className="text-sm font-semibold bg-lime-600 hover:bg-lime-500 text-black rounded-lg px-4 py-2 transition-colors disabled:opacity-50"
                        >
                          {busy ? "Processing…" : `${listing.featured ? "Extend" : "Boost"} — ${t.label} ${t.priceLabel}/${t.days}d`}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {!offer.hasBillingAccount && (
                <p className="mt-4 text-xs text-amber-400">You'll be asked to add a payment method the first time you boost.</p>
              )}
            </div>
          )}

          {/* Subscription */}
          {subscription && (
            <div className="bg-off-black border border-gray-800 rounded-xl p-6">
              <h2 className="text-sm uppercase tracking-widest text-silver-dark mb-5">Subscription</h2>
              <InfoRow label="Plan">Limelite Storefront (legacy plan)</InfoRow>
              <InfoRow label="Status"><StatusBadge status={subscription.status} /></InfoRow>
              {subscription.currentPeriodEnd && (
                <InfoRow label="Renews">
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                </InfoRow>
              )}
              {/* Existing €49 storefront subscribers keep seeing their price; label it legacy so it is not the current offer. */}
              <InfoRow label="Price">€49 / month (legacy plan) — Deploy and Creator included</InfoRow>
              <div className="mt-5 flex gap-3">
                <button
                  onClick={openPortal}
                  disabled={portalLoading}
                  className="text-sm border border-gray-700 hover:border-gray-500 text-silver-dark hover:text-silver-light rounded px-3 py-1.5 transition-colors disabled:opacity-50"
                >
                  {portalLoading ? "Loading…" : "Manage billing →"}
                </button>
              </div>
            </div>
          )}

          {/* Account */}
          <div className="bg-off-black border border-gray-800 rounded-xl p-6">
            <h2 className="text-sm uppercase tracking-widest text-silver-dark mb-5">Account</h2>
            <InfoRow label="Email">{customer?.email || "—"}</InfoRow>
            <div className="mt-4 flex gap-3 flex-wrap">
              <Link to="/limelite/deploy/account" className="text-sm text-silver-dark hover:text-silver-light border border-gray-700 hover:border-gray-500 rounded px-3 py-1.5 transition-colors">
                Full account settings →
              </Link>
              <Link to="/limelite/deploy/dashboard" className="text-sm text-silver-dark hover:text-silver-light border border-gray-700 hover:border-gray-500 rounded px-3 py-1.5 transition-colors">
                Limelite Deploy →
              </Link>
            </div>
          </div>

          {/* Provisioning help */}
          {project && projectStatus === "pending" && (
            <div className="bg-blue-900/10 border border-blue-800/30 rounded-xl p-5">
              <p className="text-sm text-blue-300 font-medium mb-2">Your store is being set up</p>
              <p className="text-silver-dark text-sm leading-relaxed">
                We're deploying the Limelite Storefront to your AWS instance. This usually takes 3–5 minutes.
                Once ready, your store URL and admin panel link will appear above.
                You'll also receive an email with your admin credentials.
              </p>
            </div>
          )}

        </div>

        <div className="mt-10">
          <DeployLegalFooter />
        </div>
      </div>
    </div>
  );
}
